/**
 * Transports & mobilité — source primaire : base consolidée nationale des
 * arrêts de transport en commun maintenue par le PAN transport.data.gouv.fr.
 *
 * Alimentée mensuellement par la Cloud Function `ingestNationalTransitStops`,
 * qui télécharge le CSV national (~440 Mo, ~2-3 M arrêts) et le bucketise
 * par geohash-5 en JSON gzippés dans
 * `gs://{default}/transit-stops/{geohash}.json`.
 *
 * Pourquoi pas OSM : lacunes dans les petits réseaux (ex. "Les Jonquilles"
 * au Touquet, desservi par Canche Transport, absent d'OSM). Le PAN agrège
 * les GTFS officiels des AO → exhaustif + à jour.
 *
 * Stratégie client :
 *   1. Calcule le geohash-5 du point de la fiche + ses 8 voisins.
 *   2. Fetch les 9 cellules en parallèle depuis Firebase Storage (public).
 *      Chaque cellule fait ~5-50 Ko après gzip, le CDN Google absorbe les
 *      hits répétés entre utilisateurs d'une même zone.
 *   3. Filtre par distance (haversine) dans le rayon demandé.
 *   4. Applique les mêmes règles métier que la variante Overpass (dedup
 *      bus bidirectionnels, caps par mode, tri par distance) pour produire
 *      un `TransportsData` compatible avec `TransportsCard`.
 *
 * Fallback : si une cellule renvoie 404 (zone non peuplée) ou 5xx, on
 * continue avec les autres. Si toutes échouent, on throw — l'infra
 * `useEnvBlock` écrira alors un `status: error` côté Firestore et la carte
 * affichera "indisponible". OSM n'est plus dans le circuit : les tests ont
 * montré qu'il est significativement moins complet pour les petits réseaux.
 */

import { haversineMeters } from "./shared";
import type { LocalityTier } from "./localityTier";
import type {
  TransportMode,
  TransportItem,
  TransportsData,
} from "./transports";
import { storage } from "../firebase";

// Storage public prefix. Aligné sur `STORAGE_PREFIX` de la Cloud Function.
const STORAGE_PREFIX = "transit-stops";
const GEOHASH_PRECISION = 5;

// Per-mode caps (mêmes valeurs que la variante Overpass).
const PER_MODE_CAP: Record<TransportMode, number> = {
  metro: 12,
  tram: 10,
  train: 8,
  bus: 25,
};
const MAX_ITEMS = 40;

// Fetch timeout par cellule. Les fichiers sont petits ; si on dépasse 10 s,
// c'est que Storage rame — on abandonne cette cellule.
const CELL_FETCH_TIMEOUT_MS = 10_000;

// ─── Public fetch ───────────────────────────────────────────────────────

export async function fetchTransportsNational(
  lat: number,
  lng: number,
  radiusM: number,
  tier: LocalityTier,
): Promise<TransportsData> {
  const center = geohashEncode(lat, lng, GEOHASH_PRECISION);
  const neighbors = geohashNeighbors(center);
  const cells = [center, ...neighbors];

  const settled = await Promise.allSettled(
    cells.map((hash) => fetchCell(hash)),
  );
  const rawStops: CellStop[] = [];
  let anySuccess = false;
  for (const r of settled) {
    if (r.status === "fulfilled" && r.value) {
      anySuccess = true;
      rawStops.push(...r.value);
    }
  }
  if (!anySuccess) {
    throw new Error("transit-stops: toutes les cellules ont échoué");
  }

  // 1) Dedup sur id + haversine filter
  const seen = new Set<string>();
  const withinRadius: TransportItem[] = [];
  for (const s of rawStops) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    const d = Math.round(haversineMeters(lat, lng, s.lat, s.lng));
    if (d > radiusM) continue;
    withinRadius.push({
      id: s.id,
      name: s.name,
      mode: s.mode,
      // Les lignes ne sont pas dans le dataset PAN "arrêts" (il faudrait
      // croiser avec trips.txt/routes.txt par GTFS). On laisse vide —
      // la carte s'adapte (pas de pill si lines=[]).
      lines: [],
      lat: s.lat,
      lng: s.lng,
      distance_m: d,
    });
  }

  // 2) Dedup bus bidirectionnels (même name, <30 m, même mode).
  const deduped = dedupeBidirectionalBus(withinRadius);
  deduped.sort((a, b) => a.distance_m - b.distance_m);

  // 3) Caps par mode puis global
  const perModeCount: Record<TransportMode, number> = {
    metro: 0, tram: 0, train: 0, bus: 0,
  };
  const items: TransportItem[] = [];
  for (const it of deduped) {
    if (perModeCount[it.mode] >= PER_MODE_CAP[it.mode]) continue;
    perModeCount[it.mode] += 1;
    items.push(it);
    if (items.length >= MAX_ITEMS) break;
  }

  const counts: Record<TransportMode, number> = {
    metro: 0, tram: 0, train: 0, bus: 0,
  };
  for (const it of deduped) counts[it.mode] += 1;

  return {
    center: { lat, lng },
    radius_m: radiusM,
    tier,
    fetchedAt: new Date().toISOString(),
    counts,
    items,
  };
}

// ─── Cell fetch ─────────────────────────────────────────────────────────

type CellStop = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  mode: TransportMode;
  network: string;
};
type CellPayload = { cell: string; count: number; stops: CellStop[] };

async function fetchCell(hash: string): Promise<CellStop[] | null> {
  const bucket = storage.app.options.storageBucket;
  if (!bucket) throw new Error("Firebase storageBucket not configured");
  const path = encodeURIComponent(`${STORAGE_PREFIX}/${hash}.json`);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${path}?alt=media`;

  try {
    const res = await fetch(url, {
      signal: AbortSignal.timeout(CELL_FETCH_TIMEOUT_MS),
    });
    if (res.status === 404) return []; // cellule vide = zone non peuplée
    if (!res.ok) throw new Error(`cell ${hash} HTTP ${res.status}`);
    const body = (await res.json()) as CellPayload;
    return body.stops ?? [];
  } catch (e) {
    // Cellules manquantes ≠ bloquantes : on laisse les autres composer
    // la réponse. L'absence totale est détectée en amont (anySuccess).
    if (e instanceof Error && /abort|timeout/i.test(e.message)) {
      return null;
    }
    if (
      e instanceof TypeError ||
      (e instanceof Error && /HTTP 404/.test(e.message))
    ) {
      return null;
    }
    throw e;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function dedupeBidirectionalBus(items: TransportItem[]): TransportItem[] {
  const out: TransportItem[] = [];
  for (const it of items) {
    if (it.mode !== "bus") {
      out.push(it);
      continue;
    }
    const dup = out.find(
      (o) =>
        o.mode === "bus" &&
        o.name.toLowerCase() === it.name.toLowerCase() &&
        haversineMeters(o.lat, o.lng, it.lat, it.lng) < 30,
    );
    if (!dup) out.push(it);
  }
  return out;
}

// ─── Geohash encode + neighbors ─────────────────────────────────────────

// Doit rester strictement aligné avec l'encoder de la Cloud Function
// (functions/src/ingestNationalTransitStops.ts) : même base32, même ordre
// lng→lat→lng…, mêmes bornes. Toute divergence = cellules manquantes.
const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
const BASE32_INDEX: Record<string, number> = {};
for (let i = 0; i < BASE32.length; i += 1) BASE32_INDEX[BASE32[i]] = i;

export function geohashEncode(
  lat: number,
  lng: number,
  precision: number,
): string {
  let minLat = -90;
  let maxLat = 90;
  let minLng = -180;
  let maxLng = 180;
  let hash = "";
  let bits = 0;
  let bit = 0;
  let even = true;
  while (hash.length < precision) {
    if (even) {
      const mid = (minLng + maxLng) / 2;
      if (lng >= mid) {
        bits = (bits << 1) + 1;
        minLng = mid;
      } else {
        bits = bits << 1;
        maxLng = mid;
      }
    } else {
      const mid = (minLat + maxLat) / 2;
      if (lat >= mid) {
        bits = (bits << 1) + 1;
        minLat = mid;
      } else {
        bits = bits << 1;
        maxLat = mid;
      }
    }
    even = !even;
    bit += 1;
    if (bit === 5) {
      hash += BASE32[bits];
      bits = 0;
      bit = 0;
    }
  }
  return hash;
}

/**
 * Retourne les 8 geohash voisins (N, NE, E, SE, S, SW, W, NW) à la même
 * précision. Implémentation simple par décodage → recalcul du centre ±
 * cellule → réencodage. Suffisant pour nos besoins (appelé 1×/fiche).
 */
export function geohashNeighbors(hash: string): string[] {
  const { lat, lng, latErr, lngErr } = geohashDecode(hash);
  const deltas = [
    [+2 * latErr, 0], // N
    [+2 * latErr, +2 * lngErr], // NE
    [0, +2 * lngErr], // E
    [-2 * latErr, +2 * lngErr], // SE
    [-2 * latErr, 0], // S
    [-2 * latErr, -2 * lngErr], // SW
    [0, -2 * lngErr], // W
    [+2 * latErr, -2 * lngErr], // NW
  ];
  const p = hash.length;
  return deltas.map(([dLat, dLng]) =>
    geohashEncode(lat + dLat, lng + dLng, p),
  );
}

function geohashDecode(hash: string): {
  lat: number;
  lng: number;
  latErr: number;
  lngErr: number;
} {
  let minLat = -90;
  let maxLat = 90;
  let minLng = -180;
  let maxLng = 180;
  let even = true;
  for (const ch of hash) {
    const idx = BASE32_INDEX[ch];
    for (let mask = 16; mask >= 1; mask >>= 1) {
      const bit = (idx & mask) !== 0;
      if (even) {
        const mid = (minLng + maxLng) / 2;
        if (bit) minLng = mid;
        else maxLng = mid;
      } else {
        const mid = (minLat + maxLat) / 2;
        if (bit) minLat = mid;
        else maxLat = mid;
      }
      even = !even;
    }
  }
  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
    latErr: (maxLat - minLat) / 2,
    lngErr: (maxLng - minLng) / 2,
  };
}
